import React, { useMemo, useState, useEffect, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { useDispatch, useSelector } from 'react-redux';
import { getUserChatMessages, getUserChats, sendMessage, generateAIReferralDM } from '../../redux/ChatSlice/chatSlice';
import { checkLinkedInConnectionStatus, fetchAllConnections, sendFirstLiMessage } from '../../redux/LIAuthSlice/liAuthSlice';

import { Search, X, ArrowLeft, MoreVertical, Paperclip, Send } from 'lucide-react';
import InboxIconSVG from '../../assets/images/Sidebar/SVG_Functions/InboxIconSVG';

const InboxModal = ({ isOpen, onClose, initialSelectedContact }) => {
    const dispatch = useDispatch();
    const messagesEndRef = useRef(null);

    const user = useSelector(state => state.auth.user);
    const { isLinkedInConnected, connections, connectionsLoading } = useSelector((state) => state.liauth);
    const {
        chats,
        chatsLoading,
        chatsError,
        chatMessages,
        chatMessagesLoading,
        chatMessagesError,
        sendMessageLoading,
        sendMessageError,
        aiReferralMessage,
        aiReferralLoading,
        aiReferralError,
    } = useSelector((state) => state.chats);

    const [selectedChat, setSelectedChat] = useState(null);
    const [text, setText] = useState("");
    const [activeTab, setActiveTab] = useState('Chat');
    const [view, setView] = useState('list');
    const [newMessageText, setNewMessageText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        // console.log("Initial selected contact: ", initialSelectedContact);
        if (isOpen && initialSelectedContact) {
            const chatForContact = chats.find(c =>
                c.attendees.some(a => a.providerId === initialSelectedContact.providerId)
            );

            if (chatForContact) {
                // Chat exists: Go to the conversation view
                const attendee = chatForContact.attendees.find(a => a.providerId === initialSelectedContact.providerId);
                setSelectedChat({
                    ...attendee,
                    chatId: chatForContact.chatId,
                    accountId: chatForContact.accountId
                });
                setView('conversation');
            } else {
                // NO chat exists: Go to the "New Message" composer view
                setView('newMessage');
                // Pre-populate the template message from the old modal
                setNewMessageText(`Hi ${initialSelectedContact.name}, I am really inspired by your work recruiting top talent at ${initialSelectedContact.organization_name}. I'd love to connect and follow more of your insights here on LinkedIn.`);
            }
        } else {
            // Reset state when modal is closed or opened without a contact
            setView('list');
            setSelectedChat(null);
        }
    }, [isOpen, initialSelectedContact, chats]);

    useEffect(() => {
        if (isOpen && user?._id) {
            dispatch(checkLinkedInConnectionStatus());
        }
    }, [dispatch, user?._id, isOpen]);

    useEffect(() => {
        if (isOpen && isLinkedInConnected?.accountId && chats.length === 0) {
            dispatch(getUserChats({ accountId: isLinkedInConnected?.accountId }));
        }
    }, [dispatch, isLinkedInConnected?.accountId, chats.length, isOpen]);

    useEffect(() => {
        if (isOpen && isLinkedInConnected?.accountId && connections.length === 0) {
            dispatch(fetchAllConnections({ accountId: isLinkedInConnected.accountId }));
        }
    }, [dispatch, isLinkedInConnected?.accountId, connections.length, isOpen]);

    useEffect(() => {
        if (isOpen && selectedChat?.chatId && !chatMessages[selectedChat.chatId]) {
            dispatch(getUserChatMessages({ chatId: selectedChat.chatId }));
        }
    }, [selectedChat, dispatch, chatMessages, isOpen]);

    // Poll messages while a real chat is open (quick "real-time" feel).
    useEffect(() => {
        const chatId = isOpen ? selectedChat?.chatId : null;
        if (!chatId) return;

        const interval = setInterval(() => {
            dispatch(getUserChatMessages({ chatId }));
        }, 7000);

        return () => clearInterval(interval);
    }, [dispatch, isOpen, selectedChat?.chatId]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const currentMessages = selectedChat ? chatMessages[selectedChat.chatId] || [] : [];

    useEffect(() => {
        if (currentMessages.length > 0) {
            scrollToBottom();
        }
    }, [currentMessages]);

    const handleSendMessage = () => {
        if (!selectedChat || !text.trim() || sendMessageLoading) return;

        dispatch(sendMessage({
            accountId: selectedChat.accountId,
            text: text,
            chatId: selectedChat.chatId
        }));
        setText("");
    };

    const handleInitiateChat = () => {
        if (!initialSelectedContact || !newMessageText.trim() || !user?._id) return;

        dispatch(sendFirstLiMessage({
            accountId: isLinkedInConnected.accountId,
            attendeeId: initialSelectedContact.providerId,
            text: newMessageText,
            userId: user._id
        }));

        // Close the modal after sending the initial message
        onClose();
    };

    const handleRegenerate = () => {
        if (!initialSelectedContact) return;
        dispatch(generateAIReferralDM({
            targetCompany: initialSelectedContact.organization_name,
            jobTitle: initialSelectedContact.title,
            recipientName: initialSelectedContact.name
        }));
    };

    useEffect(() => {
        if (aiReferralMessage) {
            setNewMessageText(aiReferralMessage);
        }
    }, [aiReferralMessage]);

    const handleBackToChats = () => {
        if (initialSelectedContact) {
            onClose(); // Always close the modal if it was opened for a specific contact
        } else {
            setSelectedChat(null);
            setView('list'); // Go back to the list
        }
    };

    const testingFunction = () => {
        console.log("Selected contact: ", initialSelectedContact);
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredChats = useMemo(() => {
        if (!normalizedQuery) return chats;
        return chats.filter((chat) => {
            const attendee = chat?.attendees?.[0];
            const haystack = `${attendee?.name || ''} ${attendee?.title || ''} ${attendee?.organization_name || ''} ${chat?.lastMessage?.text || ''}`.toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [chats, normalizedQuery]);

    const filteredConnections = useMemo(() => {
        if (!normalizedQuery) return connections;
        return connections.filter((c) => {
            const haystack = `${c?.name || ''} ${c?.title || ''} ${c?.organization_name || ''}`.toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [connections, normalizedQuery]);

    if (!isOpen) {
        return null;
    }

    const renderChatList = () => (
        <>
            {/* Header and Tabs */}
            <div className="px-6 pt-4 pb-2">
                <div className="flex justify-between items-center mb-4">
                    <div onClick={testingFunction} className='flex items-center gap-2 font-dmsans text-[#3F57E0]'>
                        <InboxIconSVG />
                        <p className="text-lg font-medium text[#3F57E0] font-dmsans">Inbox</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
                        <X size={24} />
                    </button>
                </div>
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex w-full justify-between space-x-8" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('Chat')}
                            className={`whitespace-nowrap flex w-full py-3 px-1 border-b-2 justify-center font-medium text-sm ${activeTab === 'Chat' ? 'border-blue-500 text-[#3F57E0]' : 'border-transparent text-[#3F57E0] hover:text-blue-700 hover:border-[#3F57E0]'}`}
                        >
                            Chat
                        </button>
                        <button
                            onClick={() => setActiveTab('Connections')}
                            className={`whitespace-nowrap flex w-full items-center justify-center py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'Connections' ? 'border-blue-500 text-[#3F57E0]' : 'border-transparent text-[#3F57E0] hover:text-blue-700 hover:border-[#3F57E0]'}`}
                        >
                            My Connection
                        </button>
                    </nav>
                </div>
            </div>

            {/* Search Bar */}
            <div className="px-4 mt-4">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search here..."
                        className="w-full h-10 pl-4 pr-10 font-dmsans text-sm bg-gray-100 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <Search className="w-5 h-5 text-gray-400" />
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
                {activeTab === 'Chat' ? (
                    <>
                        {chatsLoading ? (
                            <p className='text-center text-gray-500 font-dmsans'>Loading chats...</p>
                        ) : chatsError ? (
                            <p className='text-center text-rose-600 font-dmsans text-sm'>{String(chatsError)}</p>
                        ) : filteredChats.length === 0 ? (
                            <p className='text-center text-gray-500 font-dmsans'>No chats yet.</p>
                        ) : (
                            filteredChats.map((chat) => {
                                const attendee = chat.attendees?.[0];
                                if (!attendee) return null;
                                return (
                                    <div
                                        key={chat.chatId}
                                        className="flex border border-[#ECECEC] p-3 gap-3 rounded-xl hover:bg-blue-50 cursor-pointer items-center"
                                        onClick={() => {
                                            setSelectedChat({ ...attendee, chatId: chat.chatId, accountId: chat.accountId });
                                            setView('conversation');
                                        }}
                                    >
                                        <img
                                            className="w-12 h-12 rounded-full object-cover"
                                            src={attendee.profile_picture || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                                            alt={attendee.name}
                                        />
                                        <div className="flex flex-col items-start min-w-0 flex-1">
                                            <p className="font-dmsans font-semibold text-sm text-gray-800 truncate">{attendee.name}</p>
                                            <p className="font-dmsans font-normal text-xs text-gray-500 truncate">
                                                {chat.lastMessage?.text || "No messages yet"}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </>
                ) : (
                    <>
                        {connectionsLoading ? (
                            <p className='text-center text-gray-500 font-dmsans'>Loading connections...</p>
                        ) : filteredConnections.length === 0 ? (
                            <p className='text-center text-gray-500 font-dmsans'>No connections found.</p>
                        ) : (
                            filteredConnections.map((connection) => (
                                <div
                                    key={connection.providerId || connection._id}
                                    className="flex border border-[#ECECEC] p-3 gap-3 rounded-xl hover:bg-blue-50 cursor-pointer items-center"
                                    onClick={() => {
                                        // Start composing a new message to this connection
                                        setView('newMessage');
                                        setSelectedChat(null);
                                        setNewMessageText(`Hi ${connection.name || ''} — great to connect!`);
                                    }}
                                >
                                    <img
                                        className="w-12 h-12 rounded-full object-cover"
                                        src={connection.profile_picture || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                                        alt={connection.name || 'LinkedIn Member'}
                                    />
                                    <div className="flex flex-col items-start min-w-0 flex-1">
                                        <p className="font-dmsans font-semibold text-sm text-gray-800 truncate">{connection.name || 'LinkedIn Member'}</p>
                                        <p className="font-dmsans font-normal text-xs text-gray-500 truncate">
                                            {connection.title || connection.organization_name || 'Connection'}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>
        </>
    );

    const renderMessageContainer = () => (
        <>
            {/* Message Header */}
            <div className='flex items-center justify-between gap-2 p-4 border-b border-gray-200'>
                <div className='flex items-center gap-3'>
                    <button onClick={handleBackToChats} className="text-blue-600 hover:bg-blue-50 rounded-full p-1">
                        <ArrowLeft size={22} />
                    </button>
                    <img
                        className='w-10 h-10 rounded-full object-cover'
                        src={selectedChat.profile_picture || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                        alt={selectedChat.name}
                    />
                    <div className='flex flex-col items-start'>
                        <p className='font-dmsans font-bold text-base text-gray-800'>{selectedChat.name}</p>
                        <div className='flex items-center text-xs text-gray-500 font-dmsans'>
                            <span>{selectedChat.title}</span>
                            <span className='mx-2'>•</span>
                            <span>{selectedChat.organization_name}</span>
                        </div>
                    </div>
                </div>
                <button className="text-gray-500 hover:text-gray-800">
                    <MoreVertical size={22} />
                </button>
            </div>

            {/* Messages */}
            <div className='flex-1 flex flex-col p-4 overflow-y-auto bg-gray-50'>
                {chatMessagesLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <p className='font-dmsans text-gray-500'>Loading messages...</p>
                    </div>
                ) : chatMessagesError ? (
                    <div className="flex justify-center items-center h-full">
                        <p className='font-dmsans text-rose-600 text-sm'>{String(chatMessagesError)}</p>
                    </div>
                ) : (
                    currentMessages.slice().reverse().map((message) => (
                        <div
                            key={message.messageId}
                            className={`flex my-1.5 ${message.isSender ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-xs md:max-w-md px-4 py-2 rounded-2xl font-dmsans text-sm ${message.isSender
                                    ? 'bg-blue-600 text-white rounded-br-none'
                                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                                    }`}
                            >
                                <p>{message.text}</p>
                            </div>
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className='flex items-center p-3 border-t border-gray-200 bg-white'>
                <TextareaAutosize
                    className='flex-1 p-2.5 text-sm font-dmsans bg-gray-100 border border-gray-200 rounded-lg outline-none resize-none focus:ring-2 focus:ring-blue-500'
                    placeholder="Message..."
                    minRows={1}
                    maxRows={5}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />
                <button className="p-2 text-gray-500 hover:text-blue-600 ml-2">
                    <Paperclip size={20} />
                </button>
                <button
                    onClick={handleSendMessage}
                    disabled={!text.trim() || sendMessageLoading}
                    className={`p-2 ml-1 rounded-lg ${
                        !text.trim() || sendMessageLoading ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'
                    }`}
                    aria-label="Send message"
                    title={sendMessageLoading ? 'Sending...' : 'Send'}
                >
                    <Send size={18} />
                </button>
            </div>
            {sendMessageError ? (
                <div className="px-4 py-2 text-xs font-dmsans text-rose-600 border-t border-rose-100 bg-rose-50">
                    {String(sendMessageError)}
                </div>
            ) : null}
        </>
    );

    const renderNewMessageView = () => (
        <>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 font-dmsans">
                    New Message to {initialSelectedContact?.name}
                </h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
                    <X size={24} />
                </button>
            </div>

            {/* Composer */}
            <div className="flex-1 p-4 flex flex-col">
                <TextareaAutosize
                    className='w-full p-2.5 text-sm font-dmsans bg-gray-50 border border-gray-200 rounded-lg outline-none resize-none focus:ring-2 focus:ring-blue-500'
                    value={newMessageText}
                    onChange={(e) => setNewMessageText(e.target.value)}
                    minRows={8}
                />
                <button
                    onClick={handleRegenerate}
                    disabled={aiReferralLoading}
                    className={`bg-blue-500 mt-2 text-white py-1 rounded-lg ${aiReferralLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                >
                    {aiReferralLoading ? 'Regenerating...' : 'Regenerate'}
                </button>
                {aiReferralError ? (
                    <p className="mt-2 text-xs font-dmsans text-rose-600">{String(aiReferralError)}</p>
                ) : null}

            </div>

            {/* Footer / Actions */}
            <div className='flex items-center justify-end p-3 border-t border-gray-200 bg-white gap-3'>
                <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300">
                    Cancel
                </button>
                <button
                    onClick={handleInitiateChat}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-2"
                >
                    Send
                </button>
            </div>
        </>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md h-[90vh] max-h-[700px] flex flex-col overflow-hidden">
                {
                    view === 'conversation' ? renderMessageContainer() :
                        view === 'newMessage' ? renderNewMessageView() :
                            renderChatList()
                }
            </div>
        </div>
    );
};

export default InboxModal;
